import * as XLSX from 'xlsx';
import { ValidationResult } from '@/types/supabase';
import { TemplateValidationError } from '../models/errors';
import { logger, LogCategory } from '@/services/logger';

/**
 * Validates an Excel template file
 * @param file The Excel file to validate
 * @returns Validation result
 */
export async function validateExcelTemplate(file: File): Promise<ValidationResult> {
  try {
    // Check file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      return {
        isValid: false,
        errors: ['File must be an Excel file (.xlsx or .xls)']
      };
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        isValid: false,
        errors: [`File size exceeds maximum allowed (${Math.round(maxSize / 1024 / 1024)}MB)`]
      };
    }

    // Read the file
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    // Check if workbook has at least one sheet
    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
      return {
        isValid: false,
        errors: ['Excel file does not contain any sheets']
      };
    }

    const errors: string[] = [];
    const warnings: string[] = [];
    const details: Record<string, any> = {};

    // Validate each sheet
    for (const sheetName of workbook.SheetNames) {
      const sheet = workbook.Sheets[sheetName];
      
      // Check if sheet has content
      if (!sheet['!ref']) {
        warnings.push(`Sheet "${sheetName}" is empty`);
        continue;
      }

      // Perform sheet-specific validation
      const sheetValidation = validateSheet(sheet, sheetName);
      if (sheetValidation.errors && sheetValidation.errors.length > 0) {
        errors.push(...sheetValidation.errors);
      }
      if (sheetValidation.warnings && sheetValidation.warnings.length > 0) {
        warnings.push(...sheetValidation.warnings);
      }

      details[sheetName] = sheetValidation.details;
    }

    // Determine overall validity
    const isValid = errors.length === 0;

    return {
      isValid,
      errors: errors.length > 0 ? errors : undefined,
      warnings: warnings.length > 0 ? warnings : undefined,
      details
    };
  } catch (error) {
    logger.error('Error validating Excel template', LogCategory.STORAGE, { error });
    throw new TemplateValidationError('Failed to validate Excel template', {
      error: error instanceof Error ? error.message : String(error)
    });
  }
}

/**
 * Validates a specific sheet in the Excel workbook
 * @param sheet The worksheet to validate
 * @param sheetName The name of the sheet
 * @returns Validation result for the sheet
 */
function validateSheet(sheet: XLSX.WorkSheet, sheetName: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const details: Record<string, any> = {
    hasContent: !!sheet['!ref'],
    columns: []
  };

  // Skip validation for empty sheets
  if (!sheet['!ref']) {
    return { isValid: true, warnings: [`Sheet "${sheetName}" is empty`], details };
  }

  // Get sheet range
  const range = XLSX.utils.decode_range(sheet['!ref']);
  
  // Check if sheet has at least one row of data (excluding header)
  if (range.e.r < 1) {
    errors.push(`Sheet "${sheetName}" does not contain any data rows`);
  }

  // Extract header row to check for required columns
  const headerRow: string[] = [];
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    const cellValue = sheet[cellAddress]?.v;
    headerRow.push(cellValue ? String(cellValue).trim() : '');
    
    if (cellValue) {
      details.columns.push(String(cellValue).trim());
    }
  }

  // Check for recommended columns based on sheet name
  // This can be customized based on the specific requirements for different template types
  const recommendedColumns: Record<string, string[]> = {
    'default': ['Name', 'Date'],
    'Calendar': ['Activity', 'Date', 'Description'],
    'Participants': ['Name', 'Department', 'Role'],
    'Schedule': ['Task', 'Start Date', 'End Date', 'Assignee']
  };

  // Determine which set of recommended columns to check
  const columnsToCheck = recommendedColumns[sheetName] || recommendedColumns.default;
  
  // Check for missing recommended columns
  const missingColumns = columnsToCheck.filter(col =>
    !headerRow.some(header => header.toLowerCase() === col.toLowerCase())
  );

  if (missingColumns.length > 0) {
    // Add as warnings instead of errors to allow the upload to proceed
    warnings.push(`Sheet "${sheetName}" is missing recommended columns: ${missingColumns.join(', ')}`);
  }

  // Add sheet-specific validation here as needed
  // For example, validate date formats, check for duplicate entries, etc.

  return {
    isValid: errors.length === 0,
    errors: errors.length > 0 ? errors : undefined,
    warnings: warnings.length > 0 ? warnings : undefined,
    details
  };
}

/**
 * Extracts metadata from an Excel template
 * @param file The Excel file
 * @returns Extracted metadata
 */
export async function extractTemplateMetadata(file: File): Promise<Record<string, any>> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    
    const metadata: Record<string, any> = {
      sheetNames: workbook.SheetNames,
      sheetCount: workbook.SheetNames.length,
      fileSize: file.size,
      fileType: file.type,
      fileName: file.name
    };

    // Extract additional metadata if available
    if (workbook.Props) {
      metadata.title = workbook.Props.Title;
      metadata.subject = workbook.Props.Subject;
      metadata.author = workbook.Props.Author;
      metadata.company = workbook.Props.Company;
      metadata.createdDate = workbook.Props.CreatedDate;
      metadata.modifiedDate = workbook.Props.ModifiedDate;
    }

    return metadata;
  } catch (error) {
    logger.error('Error extracting template metadata', LogCategory.STORAGE, { error });
    throw new TemplateValidationError('Failed to extract template metadata', {
      error: error instanceof Error ? error.message : String(error)
    });
  }
}